<template>
    <Spinner v-if="displaySpinner" />
    <div class="title-and-controls">
        <div class="page-title">
            <span class="page-title">
                <i class="pi pi-check-square" />
                <h1>{{ taskName }}</h1>
            </span>
            <Tag
                icon="pi pi-exclamation-triangle"
                severity="warn"
                value="Archived"
                v-if="isArchived"
            ></Tag>
        </div>
        <div class="controls">
            <template v-if="isArchived">
                <Button
                    severity="danger"
                    icon="pi pi-trash"
                    label="Delete"
                    @click="handleDeleteArchive"
                />
            </template>
            <template v-else>
                <Button
                    v-if="description && !description.content"
                    v-tooltip.bottom="{
                        value: 'Will generate a first draft of the description based on the context.',
                        showDelay: 100,
                    }"
                    severity="secondary"
                    icon="pi pi-sparkles"
                    :label="
                        description?.content
                            ? 'Regenerate'
                            : 'Generate first draft'
                    "
                    @click="generateTaskDescription"
                />
            </template>
        </div>
    </div>
    <section class="description" :class="{ 'edit': isEditing }">
        <TextAppArtefact
            v-if="task?.identity && description"
            v-model="description"
            :entityId="task.identity"
            :entityType="EntityType.TASK"
            :isEditing="isEditing"
        />
    </section>
    <section :class="{ 'edit': isEditing }">
        <Documents
            v-if="task"
            :isEditing="isEditing"
            entityType="task"
            :entity="task"
            :artefacts="artefacts"
        />
    </section>
</template>

<script setup lang="ts">
import { baseSuccessToastMessage } from "@/primevue/init";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import Spinner from "@/shared/Spinner.vue";
import Documents from "@/components/Documents.vue";
import { computed, ref, watch } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { useTaskManager, useTaskStore } from "@/stores/task-store";
import { useArtefactsManager } from "@/lib/artefacts/user";
import { useEditManager } from "@/composables/edit-manager";
import { EntityType } from "@/stores/entity-store";
import { useGenerateDescriptionDialog } from "@/composables/generate-description";

const toast = useToast();
const { deleteTask } = useTaskStore();
const { isEditing } = useEditManager();
const { isFetching, description, task } = useTaskManager();
const { artefacts } = useArtefactsManager();
const { generateContent } = useGenerateDescriptionDialog();

const confirm = useConfirm();

const displaySpinner = ref(false);

const isArchived = computed(() => {
    return task.value?.is_archived;
});

const handleDeleteArchive = async (event: Event) => {
    if (!isArchived.value) {
        toast.add({
            severity: "error",
            summary: "Unable to delete task. Task is not archived.",
        });
        return;
    }

    confirm.require({
        target: event.currentTarget as HTMLElement,
        message: "Are you sure you want to permanently delete this task?",
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        accept: async () => {
            try {
                displaySpinner.value = true;
                await deleteTask();

                toast.add({
                    ...baseSuccessToastMessage,
                    summary: "Task has been permanently deleted",
                });
            } catch {
                toast.add({
                    severity: "error",
                    summary: "Could not delete task",
                });
            } finally {
                displaySpinner.value = false;
            }
        },
    });
};

const taskName = computed(() => {
    if (!task.value) {
        return "...";
    }

    return task.value.name || task.value.bpmn_id;
});

const generateTaskDescription = async () => {
    if (!task.value) {
        return;
    }
    await generateContent(
        task.value.identity,
        EntityType.TASK,
        description.value?.updateContent.bind(description.value)
    );
};

watch(isFetching, (newValue) => (displaySpinner.value = newValue), {
    immediate: true,
});
</script>

<style scoped lang="scss">
.controls {
    text-wrap-mode: nowrap;
}

:deep(.generation-placeholder) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 2rem;
}
.page-title {
    margin-left: 1rem;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 0;

    i {
        font-size: 1.6rem;
    }
    .p-tag {
        margin-left: 0.5rem;
    }
}

section {
    border: 1px solid transparent;
    position: relative;
    padding: 1rem;

    &.description {
        padding-top: 0;
        z-index: 3;
    }

    &.edit {
        border-color: var(--stekz-outline);
        border-radius: 0.5rem;

        &.description {
            padding: 0;
        }
    }
}
</style>
